<!--
  RoomScanPanel — the product surface of the capture flow (card `t_90febbcf`,
  A2; plan `C:/Projects/Otto/docs/plans/3D-ROOM-SCANNING.md` §5.1).

  It does three things and no more:

   1. asks `roomScanSupported()` BEFORE it offers anything, and paints the honest
      answer when this device cannot scan (plan §7: LiDAR-only, iOS-only — "a
      non-LiDAR device gets an honest 'requires iPhone Pro (LiDAR)' state");
   2. starts and drives a capture: one room (iOS 16+) or a whole floor, room by
      room (iOS 17+ — the native side keeps one AR session across the rooms, so
      `startCapture` is called again per room and `stopCapture` merges them);
   3. lists the captures that are on this device, with their v0 payload
      (`scanPath` — `scan.json`, schema `torik.scan/v0`), because a capture now
      exists nowhere else: the cloud upload is card A3's.

  The native RoomPlan UI is a full-screen view controller presented over the
  WebView, so the states here are the ones *around* it: before it opens, and
  after it has written its files. Closing this panel during a structure keeps the
  native session alive — reopening and pressing "Scan another room" continues it
  (the same captureId, the same AR world space).
-->
<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center"
      @mousedown.self="$emit('close')">
      <div
        class="glass-surface flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl dark:bg-gray-800">
        <!-- Header -->
        <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div class="flex min-w-0 items-center gap-2">
            <span class="material-symbols-outlined text-xl text-gray-900 dark:text-white">view_in_ar</span>
            <h3 class="truncate text-lg font-semibold text-gray-900 dark:text-white">
              {{ t('views.roomScan.title') }}
            </h3>
          </div>
          <button
            type="button"
            class="flex-shrink-0 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
            :title="t('common.close')"
            @click="$emit('close')">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <!-- 1. The capability probe -->
          <p v-if="phase === 'checking'" class="text-sm text-gray-500 dark:text-gray-400">
            {{ t('views.roomScan.checking') }}
          </p>

          <!-- 2. The honest unsupported state (the only thing a non-LiDAR device sees) -->
          <div v-else-if="phase === 'unsupported'" class="space-y-2" data-room-scan-unsupported>
            <p class="text-sm font-medium text-gray-900 dark:text-white">
              {{ t('views.roomScan.unsupportedTitle') }}
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-300">
              {{ platform === 'ios' ? t('views.roomScan.unsupportedLidar') : t('views.roomScan.unsupportedPlatform') }}
            </p>
          </div>

          <!-- 3. The capture flow -->
          <template v-else>
            <div
              v-if="error"
              class="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <p class="text-sm font-medium text-red-800 dark:text-red-200">{{ t('views.roomScan.errorTitle') }}</p>
              <p class="mt-1 break-words text-sm text-red-700 dark:text-red-300">{{ error }}</p>
            </div>

            <!-- Nothing running: name it and start -->
            <template v-if="phase === 'idle'">
              <div>
                <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ t('views.roomScan.nameLabel') }}
                </label>
                <input
                  v-model="captureName"
                  type="text"
                  :placeholder="t('views.roomScan.namePlaceholder')"
                  class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>

              <div class="space-y-2">
                <button
                  type="button"
                  class="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-3 font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="busy"
                  data-room-scan-start
                  @click="startCapture('room')">
                  <span class="material-symbols-outlined text-xl">view_in_ar</span>
                  {{ t('views.roomScan.startRoom') }}
                </button>
                <button
                  v-if="support?.structureSupported"
                  type="button"
                  class="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-3 font-medium text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
                  :disabled="busy"
                  data-room-scan-start-floor
                  @click="startCapture('structure')">
                  <span class="material-symbols-outlined text-xl">floor</span>
                  {{ t('views.roomScan.startStructure') }}
                </button>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ t('views.roomScan.startHint') }}</p>
              </div>
            </template>

            <!-- The native UI is up -->
            <template v-else-if="phase === 'capturing'">
              <div class="flex items-center gap-3">
                <span
                  class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-b-transparent"></span>
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{
                    mode === 'structure' ? t('views.roomScan.capturingRoomFloor') : t('views.roomScan.capturingRoom')
                  }}
                </p>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300">{{ t('views.roomScan.capturingHint') }}</p>
            </template>

            <!-- A floor, room by room: rooms on disk, nothing live -->
            <template v-else-if="phase === 'structure'">
              <p class="text-sm font-medium text-gray-900 dark:text-white">
                {{ t('views.roomScan.structureRooms', { count: structureRooms }) }}
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-300">{{ t('views.roomScan.structureHint') }}</p>
              <div class="space-y-2">
                <button
                  type="button"
                  class="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-3 font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="busy"
                  data-room-scan-next-room
                  @click="startCapture('structure')">
                  <span class="material-symbols-outlined text-xl">add_home</span>
                  {{ t('views.roomScan.scanAnother') }}
                </button>
                <button
                  type="button"
                  class="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-3 font-medium text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
                  :disabled="busy"
                  data-room-scan-finish
                  @click="finishStructure">
                  <span class="material-symbols-outlined text-xl">check</span>
                  {{ t('views.roomScan.finish') }}
                </button>
              </div>
            </template>

            <!-- The capture that just finished -->
            <div
              v-if="lastCapture"
              class="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
              data-room-scan-saved>
              <p class="text-sm font-medium text-gray-900 dark:text-white">
                {{ t('views.roomScan.saved', { label: lastCapture.label, count: lastCapture.roomCount }) }}
              </p>
              <p class="mt-1 break-all text-xs text-gray-500 dark:text-gray-400">
                {{ t('views.roomScan.scanFile') }}: {{ fileName(lastCapture.scanPath) }}
              </p>
            </div>
          </template>

          <!-- On this device -->
          <div v-if="phase !== 'checking'" class="border-t border-gray-200 pt-3 dark:border-gray-700">
            <p class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ t('views.roomScan.capturesTitle') }}
            </p>
            <p v-if="captures.length === 0" class="text-sm text-gray-500 dark:text-gray-400">
              {{ t('views.roomScan.capturesEmpty') }}
            </p>
            <ul v-else class="space-y-2">
              <li
                v-for="capture in captures"
                :key="capture.id"
                class="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
                <div class="min-w-0">
                  <p class="truncate text-sm text-gray-900 dark:text-white">{{ capture.label }}</p>
                  <p class="truncate text-xs text-gray-500 dark:text-gray-400">
                    {{
                      capture.mode === 'structure' ? t('views.roomScan.modeStructure') : t('views.roomScan.modeRoom')
                    }}
                    · {{ t('views.roomScan.rooms', { count: capture.roomCount }) }} ·
                    {{ formatDate(capture.createdAt) }}
                  </p>
                </div>
                <button
                  type="button"
                  class="flex-shrink-0 text-gray-400 transition-colors hover:text-red-600 dark:hover:text-red-400"
                  :title="t('common.delete')"
                  @click="removeCapture(capture)">
                  <span class="material-symbols-outlined text-lg">delete</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { PluginListenerHandle } from '@capacitor/core';
import { useMessageBox } from '../../composables/useMessageBox';
import {
  deleteRoomCapture,
  listRoomCaptures,
  onCaptureCancelled,
  onCaptureFailed,
  onCaptureFinished,
  onRoomCaptured,
  roomScanSupported,
  startRoomCapture,
  stopRoomCapture,
  type RoomScanCapture,
  type RoomScanCaptureSummary,
  type RoomScanMode,
  type RoomScanSupportInfo,
} from '../../plugins/roomScan';

const props = defineProps<{ open: boolean; platform: string }>();
defineEmits<{ close: [] }>();

const { t } = useI18n();
const { confirm } = useMessageBox();

/**
 * The panel's own state machine. `idle` covers both "nothing started" and "the
 * last capture is on disk"; `structure` is the one state only a floor has
 * (rooms captured, nothing live).
 */
type Phase = 'checking' | 'unsupported' | 'idle' | 'capturing' | 'structure';

const phase = ref<Phase>('checking');
const support = ref<RoomScanSupportInfo | null>(null);
const mode = ref<RoomScanMode>('room');
const captures = ref<RoomScanCaptureSummary[]>([]);
const lastCapture = ref<RoomScanCapture | null>(null);
const structureRooms = ref(0);
const captureName = ref('');
const busy = ref(false);
const error = ref('');

const isIos = computed(() => props.platform === 'ios');

/** `file:///…/RoomScan/<id>/scan.json` → `scan.json`. */
function fileName(path: string): string {
  return path.split('/').pop() ?? path;
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** The capability probe — the one call that is safe on every platform. */
async function probe(): Promise<void> {
  phase.value = 'checking';
  error.value = '';
  try {
    support.value = await roomScanSupported();
    // The honest answer, not a hidden button (plan §7).
    phase.value = support.value.supported ? 'idle' : 'unsupported';
  } catch (probeError) {
    // A probe that throws is a plugin that is not there: it cannot scan either.
    support.value = { supported: false, structureSupported: false, platform: props.platform, minimumIOS: '16.0' };
    phase.value = 'unsupported';
    error.value = messageOf(probeError);
  }
}

async function refreshCaptures(): Promise<void> {
  try {
    captures.value = await listRoomCaptures();
  } catch {
    // A store that cannot be listed is not worth a red box: the capture flow
    // itself still works, and the list is the panel's least important half.
    captures.value = [];
  }
}

async function startCapture(requested: RoomScanMode): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    const label = captureName.value.trim();
    await startRoomCapture(label ? { mode: requested, label } : { mode: requested });
    mode.value = requested;
    phase.value = 'capturing';
  } catch (startError) {
    error.value = messageOf(startError);
    // The native session may already hold rooms from an earlier round: say so
    // instead of pretending the flow is fresh.
    if (structureRooms.value > 0) phase.value = 'structure';
  } finally {
    busy.value = false;
  }
}

/** Merges the floor's rooms into one capture (StructureBuilder, iOS 17). */
async function finishStructure(): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    lastCapture.value = await stopRoomCapture();
    structureRooms.value = 0;
    captureName.value = '';
    phase.value = 'idle';
    await refreshCaptures();
  } catch (finishError) {
    error.value = messageOf(finishError);
  } finally {
    busy.value = false;
  }
}

async function removeCapture(capture: RoomScanCaptureSummary): Promise<void> {
  const confirmed = await confirm({
    message: t('views.roomScan.deleteConfirm', { label: capture.label }),
    variant: 'danger',
  });
  if (!confirmed) return;
  await deleteRoomCapture(capture.id);
  if (lastCapture.value?.id === capture.id) lastCapture.value = null;
  await refreshCaptures();
}

// ── The native events ──────────────────────────────────────────────────────
// Attached once, for the panel's whole life: a capture runs while this panel is
// behind the native full-screen UI, and its outcome arrives as an event, not as
// a promise.
const listeners: PluginListenerHandle[] = [];

function clearError(): void {
  error.value = '';
}

onMounted(async () => {
  if (!isIos.value) {
    // No native half to ask (the entry is iOS-only, plan §7) — answer honestly
    // without touching the plugin.
    phase.value = 'unsupported';
    await refreshCaptures();
    return;
  }
  listeners.push(
    await onRoomCaptured((capture) => {
      // One room of a floor is on disk; more may follow.
      structureRooms.value = capture.roomCount;
      mode.value = 'structure';
      phase.value = 'structure';
    }),
    await onCaptureFinished(async (capture) => {
      lastCapture.value = capture;
      structureRooms.value = 0;
      captureName.value = '';
      phase.value = 'idle';
      await refreshCaptures();
    }),
    await onCaptureCancelled((failure) => {
      clearError();
      structureRooms.value = 0;
      phase.value = support.value?.supported ? 'idle' : 'unsupported';
      if (failure.id) void refreshCaptures();
    }),
    await onCaptureFailed((failure) => {
      error.value = failure.message ?? t('views.roomScan.errorTitle');
      structureRooms.value = 0;
      phase.value = support.value?.supported ? 'idle' : 'unsupported';
    }),
  );
});

onUnmounted(() => {
  for (const listener of listeners) void listener.remove();
  listeners.length = 0;
});

// Opening the panel probes the device and re-reads the store — a capture made in
// the previous round is on the list by then.
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    clearError();
    void refreshCaptures();
    if (isIos.value && phase.value !== 'capturing' && phase.value !== 'structure') void probe();
  },
  { immediate: true },
);
</script>
